<template>
    <section class="bo-card">
        <FormTabHeader
            icon="fal fa-code-branch"
            :title="$t('backoffice_infos.sections.deployments')"
        />
        <div class="bo-body">
            <div v-for="form in forms" :key="'deploy-' + form.id" class="bod-form">
                <div class="bod-form-header">
                    <div class="bod-form-title">
                        <i class="fal fa-file-alt"></i>
                        {{ form.name }}
                    </div>
                    <router-link
                        v-if="form.can_edit"
                        :to="`/forms/${form.id}/app/create`"
                        class="bo-action-btn bo-action-btn--primary"
                    >
                        <i class="fal fa-plus"></i>
                        {{ $t('backoffice_infos.add_deployment') }}
                    </router-link>
                </div>
                <div v-if="form.deployments && form.deployments.length" class="bo-table-wrapper">
                    <table class="bo-table">
                        <thead>
                            <tr>
                                <th>{{ $t('backoffice_infos.deploy.name') }}</th>
                                <th>{{ $t('backoffice_infos.deploy.url') }}</th>
                                <th>{{ $t('backoffice_infos.deploy.lang') }}</th>
                                <th>{{ $t('backoffice_infos.deploy.active') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="app in form.deployments" :key="app.id">
                                <td class="bod-cell-name">{{ app.name }}</td>
                                <td class="bod-cell-url">
                                    <a :href="app.client_url" target="_blank" rel="noopener">
                                        {{ app.client_url }}
                                        <i class="fal fa-external-link"></i>
                                    </a>
                                </td>
                                <td>
                                    <span class="bo-lang-pill">{{ app.lang }}</span>
                                </td>
                                <td>
                                    <span class="bo-pill" :class="app.active ? 'bo-pill--yes' : 'bo-pill--no'">
                                        {{ app.active ? $t('common.yes') : $t('common.no') }}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p v-else class="bo-empty">{{ $t('backoffice_infos.no_deployments') }}</p>
            </div>
        </div>
    </section>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import FormTabHeader from '@/components/Model/Form/FormComponents/FormTabHeader'

export default {
    name: 'BackofficeDeployments',
    components: { FormTabHeader },
    props: {
        forms: { type: Array, required: true }
    }
}
</script>

<style scoped>
.bo-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
}

.bo-body {
    padding: var(--v2-space-4) var(--v2-space-5);
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-4);
}

.bod-form {
    padding-bottom: var(--v2-space-4);
    border-bottom: 1px solid var(--v2-border-subtle);
}

.bod-form:last-child {
    padding-bottom: 0;
    border-bottom: none;
}

.bod-form-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--v2-space-3);
    margin-bottom: 12px;
}

.bod-form-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
}

.bod-form-title i {
    color: var(--v2-primary);
    font-size: 0.85rem;
}

.bo-table-wrapper {
    overflow-x: auto;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
}

.bo-table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--v2-text-xs-size);
}

.bo-table th {
    text-align: left;
    padding: 8px 12px;
    background: var(--v2-surface-1);
    color: var(--v2-text-3);
    font-weight: var(--v2-font-weight-semibold);
    text-transform: uppercase;
    letter-spacing: 0.02em;
    font-size: 0.68rem;
    border-bottom: 1px solid var(--v2-border);
}

.bo-table td {
    padding: 10px 12px;
    border-bottom: 1px solid var(--v2-border-subtle);
    color: var(--v2-text-2);
    vertical-align: middle;
}

.bo-table tr:last-child td {
    border-bottom: none;
}

.bod-cell-name {
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
}

.bod-cell-url a {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: var(--v2-primary);
    text-decoration: none !important;
    transition: color var(--v2-transition-fast);
    max-width: 320px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.bod-cell-url a:hover {
    color: var(--v2-primary-hover, #0e7490);
    text-decoration: underline !important;
}

.bod-cell-url i {
    font-size: 0.65rem;
    flex-shrink: 0;
}

.bo-lang-pill {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: var(--v2-radius-sm);
    background: var(--v2-surface-2);
    color: var(--v2-text-2);
    font-size: 0.66rem;
    font-weight: var(--v2-font-weight-bold);
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.bo-pill {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: var(--v2-font-weight-semibold);
}

.bo-pill--yes {
    background: rgba(16, 185, 129, 0.12);
    color: #059669;
}

.bo-pill--no {
    background: rgba(239, 68, 68, 0.12);
    color: #dc2626;
}

.bo-action-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 28px;
    padding: 0 10px;
    border-radius: var(--v2-radius-sm);
    font-size: 0.72rem;
    font-weight: var(--v2-font-weight-semibold);
    background: transparent;
    border: 1px solid var(--v2-border);
    color: var(--v2-text-2);
    text-decoration: none !important;
    cursor: pointer;
    transition: all var(--v2-transition-fast);
    flex-shrink: 0;
}

.bo-action-btn i {
    font-size: 0.72rem;
}

.bo-action-btn:hover {
    border-color: var(--v2-primary);
    color: var(--v2-primary);
    background: var(--v2-primary-soft);
}

.bo-action-btn--primary {
    background: var(--v2-primary);
    color: #fff !important;
    border-color: var(--v2-primary);
}

.bo-action-btn--primary i {
    color: #fff !important;
}

.bo-action-btn--primary:hover {
    background: var(--v2-primary-hover, #0e7490);
    color: #fff !important;
    border-color: var(--v2-primary-hover, #0e7490);
}

.bo-empty {
    margin: 0;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-4);
    font-style: italic;
}

@media (max-width: 768px) {
    .bo-body {
        padding: var(--v2-space-3) var(--v2-space-4);
    }

    .bod-form-header {
        flex-direction: column;
        align-items: flex-start;
    }
}
</style>
